import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { logStatChange } from '../lib/statHistory';
import { supabase, CompanyState } from '../lib/supabase';
import { useAuth } from './AuthContext';

type CompanyContextType = {
  companyState: CompanyState | null;
  workspaceId: string | null;
  loading: boolean;
  updateCapital: (delta: number) => Promise<void>;
  updateReputation: (delta: number) => Promise<void>;
  updateCreditScore: (delta: number) => Promise<void>;
  logEvent: (eventId: string, eventType: string, eventTitle: string, chosenAction: string, capDelta: number, repDelta: number) => Promise<void>;
  refreshState: () => Promise<void>;
};

const CompanyContext = createContext<CompanyContextType | null>(null);

const SOLO_WORKSPACE_KEY = 'kap_solo_workspace_id';

export function CompanyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [companyState, setCompanyState] = useState<CompanyState | null>(null);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // 워크스페이스 확인 또는 자동 생성
  const ensureWorkspace = useCallback(async (userId: string): Promise<string | null> => {
    const stored = localStorage.getItem(SOLO_WORKSPACE_KEY);
    if (stored) {
      const { data } = await supabase.from('workspaces').select('id').eq('id', stored).maybeSingle();
      if (data) return data.id as string;
    }
    const { data: existing } = await supabase.from('workspaces').select('id').eq('owner_id', userId).maybeSingle();
    if (existing) {
      localStorage.setItem(SOLO_WORKSPACE_KEY, existing.id);
      return existing.id as string;
    }
    // 자동 생성
    const playMode = (localStorage.getItem('kap_play_mode') ?? 'B') as 'A' | 'B';
    const companyType = localStorage.getItem('kap_company_type') ?? 'MusicLabel';
    const { data: newWs, error } = await supabase.from('workspaces').insert({
      name: 'My Workspace',
      owner_id: userId,
      play_mode: playMode,
      company_type: companyType,
    }).select('id').maybeSingle();
    if (error || !newWs) return null;
    localStorage.setItem(SOLO_WORKSPACE_KEY, newWs.id);
    return newWs.id as string;
  }, []);

  const fetchState = useCallback(async (userId: string, wsId: string) => {
    const { data } = await supabase
      .from('company_states')
      .select('*')
      .eq('workspace_id', wsId)
      .eq('user_id', userId)
      .maybeSingle();

    if (data) {
      setCompanyState(data as CompanyState);
    } else {
      const playMode = localStorage.getItem('kap_play_mode') ?? 'B';
      const initCapital = playMode === 'B' ? 50000 : 0;
      const { data: newState } = await supabase.from('company_states').insert({
        workspace_id: wsId,
        user_id: userId,
        capital: initCapital,
        reputation: 5,
        credit_score: 80,
        active_projects: 0,
      }).select().maybeSingle();
      if (newState) setCompanyState(newState as CompanyState);
    }
  }, []);

  const init = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    const wsId = await ensureWorkspace(user.id);
    if (wsId) {
      setWorkspaceId(wsId);
      await fetchState(user.id, wsId);
    }
    setLoading(false);
  }, [user, ensureWorkspace, fetchState]);

  useEffect(() => { init(); }, [init]);

  const updateCapital = async (delta: number) => {
    if (!companyState) return;
    const newCapital = Math.max(0, companyState.capital + delta);
    const { error } = await supabase
      .from('company_states')
      .update({ capital: newCapital, updated_at: new Date().toISOString() })
      .eq('id', companyState.id);
    if (!error) setCompanyState(prev => prev ? { ...prev, capital: newCapital } : prev);
  };

  const updateReputation = async (delta: number) => {
    if (!companyState) return;
    const newRep = Math.min(100, Math.max(0, companyState.reputation + delta));
    const { error } = await supabase
      .from('company_states')
      .update({ reputation: newRep, updated_at: new Date().toISOString() })
      .eq('id', companyState.id);
    if (!error) setCompanyState(prev => prev ? { ...prev, reputation: newRep } : prev);
  };

  const updateCreditScore = async (delta: number) => {
    if (!companyState) return;
    const newScore = Math.min(100, Math.max(0, companyState.credit_score + delta));
    const { error } = await supabase
      .from('company_states')
      .update({ credit_score: newScore, updated_at: new Date().toISOString() })
      .eq('id', companyState.id);
    if (!error) setCompanyState(prev => prev ? { ...prev, credit_score: newScore } : prev);
  };

  const logEvent = async (
    eventId: string,
    eventType: string,
    eventTitle: string,
    chosenAction: string,
    capDelta: number,
    repDelta: number
  ) => {
    if (!workspaceId || !user) return;
    await supabase.from('event_logs').insert({
      workspace_id: workspaceId,
      user_id: user.id,
      event_id: eventId,
      event_type: eventType,
      event_title: eventTitle,
      chosen_action: chosenAction,
      capital_change: capDelta,
      reputation_change: repDelta,
    });
    // 수치 업데이트 + 히스토리 기록
    if (repDelta !== 0) logStatChange('reputation', repDelta, `이벤트: ${eventTitle}`);
    if (capDelta !== 0) logStatChange('capital', capDelta, `이벤트: ${eventTitle}`);
    await updateCapital(capDelta);
    await updateReputation(repDelta);
  };

  const refreshState = async () => {
    if (user && workspaceId) await fetchState(user.id, workspaceId);
  };

  return (
    <CompanyContext.Provider value={{
      companyState,
      workspaceId,
      loading,
      updateCapital,
      updateReputation,
      updateCreditScore,
      logEvent,
      refreshState,
    }}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error('useCompany must be used within CompanyProvider');
  return ctx;
}
